package top.wcpe.allincore.dsl.compiler.api

/**
 * 编译器对外暴露的元数据集中地。
 *
 * 设计目的：
 * - **IDE 端不再硬编码**：M4 起 IDE 的关键字补全、属性建议、枚举值校验等都依赖
 *   这里的常量；编译器演化（新增元素 kind、修改 toast 位置取值等）后 IDE 端
 *   通过依赖更新自动感知，不需要改 IDE 代码
 * - **SSOT**：编译器内部（如 [top.wcpe.allincore.dsl.compiler.semantic.ElementValidator]）
 *   引用本对象，避免常量散落
 *
 * 命名约定：所有常量都是 `Set<String>`，方便 `contains` 判断，也方便序列化。
 */
object IdeMetadata {

    /**
     * 元素 kind 白名单。
     *
     * 与 ui-runtime 的 `IrElementFactory` 注册保持一致；新增 kind 时两侧必须同步。
     */
    val allElementKinds: Set<String> = setOf(
        "box", "row", "column", "text", "button",
        "checkbox",
        "progressbar", "image", "divider",
        "splitter",
        "slider",
        "textfield",
        "switch",
        "radio", "radiogroup",
        "dropdown", "option",
        "scrollview",
        "stack", "virtualList", "grid",
        "virtualGrid", "virtualMasonry", "virtualTree", "multiColumnList",
        "pageview", "carousel",
        // Phase 3 C 类装饰组件
        "pullToRefresh", "infiniteScroll", "collapsingHeader",
        "zoomable", "autoScroll", "dragReorder",
        "tabview", "tab",
        "itemstack",
        "spacer",
        "slot",
        "slotGrid",
        "inventory",
        "entityPreview", "modelPreview",
        "canvas",
        "modal", "toast",
        "item",
    )

    /** modelPreview 固定的七项作者专有属性。 */
    val modelPreviewAuthorProps: Set<String> = setOf(
        "source", "texture", "scale", "rotationX", "rotationY", "interactive", "backgroundColor",
    )

    /** modelPreview 编译器注入的保留内部属性。 */
    const val modelPreviewBaseProp: String = "__modelPreviewBase"

    /** 菜单 overlay 解析专用子元素（item / divider）。 */
    val menuChildKinds: Set<String> = setOf("item", "divider")

    /** toast `kind` 字面量合法取值。 */
    val toastKinds: Set<String> = setOf("info", "success", "warning", "error")

    /** toast `position` 字面量合法取值。 */
    val toastPositions: Set<String> = setOf("top", "bottom", "center")

    /** button `variant` 字面量合法取值。 */
    val buttonVariants: Set<String> = setOf("filled", "tonal", "outlined", "text", "elevated")

    /** grid `gridAutoFlow` 字面量合法取值（隐式轨道自动流向）。 */
    val gridAutoFlowValues: Set<String> = setOf("row", "column", "row-dense", "column-dense")

    /** grid `justifyItems` 字面量合法取值（子项主轴对齐，复用 Align 语义）。 */
    val gridJustifyItemsValues: Set<String> = setOf("start", "center", "end", "stretch")

    /**
     * text `typography` 字面量合法取值（Material Design 3 typography 角色）。
     */
    val textTypographyRoles: Set<String> = setOf(
        "displayLarge", "displayMedium", "displaySmall",
        "headlineLarge", "headlineMedium", "headlineSmall",
        "titleLarge", "titleMedium", "titleSmall",
        "bodyLarge", "bodyMedium", "bodySmall",
        "labelLarge", "labelMedium", "labelSmall",
    )

    /**
     * 通用元素属性白名单（所有元素 kind 都可使用）。
     *
     * - `visible`：控制可见性（Boolean）
     * - `opacity`：控制透明度（Number 0..1）
     * - `pointerEvents`：是否拦截指针事件（Boolean）
     * - `overflow`：溢出处理方式（EnumValues：visible/hidden/clip）
     * - `aboveItem`：是否渲染在物品图标之上（Boolean，默认 false）
     * - `animateLayout`：是否参与布局动画 FLIP（Boolean，默认 false）
     * - `gridColumn` / `gridRow`：grid 子项放置（String，写在子元素上）
     * - `aspectRatio`：宽高比约束（Number / 比值字符串，不限 grid）
     */
    val universalElementProps: Set<String> = setOf(
        "visible",
        "opacity",
        "pointerEvents",
        "overflow",
        "aboveItem",
        "animateLayout",
        "gridColumn",
        "gridRow",
        "aspectRatio",
    )

    // ===== 通用样式属性的枚举取值集合（与 ui/runtime 的枚举一一对应；新增枚举值时两侧同步） =====

    /** `align`（交叉轴对齐）合法取值，对应 runtime `Align` 枚举。 */
    val alignValues: Set<String> = setOf("start", "center", "end", "stretch")

    /**
     * `justify`（主轴对齐）合法取值，对应 runtime `Justify` 枚举。
     *
     * 注意：与 `align` 不同，`justify` **不接受 `stretch`**（runtime 显式拒绝）。
     */
    val justifyValues: Set<String> = setOf(
        "start",
        "center",
        "end",
        "space-between",
        "space-around",
        "space-evenly",
    )

    /** `wrap`（换行模式）合法取值，对应 runtime `FlexWrap` 枚举。 */
    val flexWrapValues: Set<String> = setOf("nowrap", "wrap", "wrap-reverse")

    /** `overflow`（溢出处理）合法取值；`clip` 是 `hidden` 的别名。 */
    val overflowValues: Set<String> = setOf("visible", "hidden", "clip")

    /** `gradientType`（渐变类型）合法取值，对应 runtime `GradientType` 枚举。 */
    val gradientTypeValues: Set<String> = setOf("none", "linear", "radial")

    /** `cursor`（鼠标光标形状）合法取值，对应 runtime `CursorType` 枚举（短横线写法）。 */
    val cursorValues: Set<String> = setOf(
        "default", "pointer", "text", "move",
        "resize-h", "resize-v", "resize-nwse", "resize-nesw",
        "not-allowed", "wait",
    )

    /** `mask`（遮罩类型）合法取值，对应 runtime `MaskType` 枚举。 */
    val maskTypeValues: Set<String> = setOf(
        "none",
        "rect",
        "circle",
        "wave",
        "pie",
        "invert_rect",
        "invert_circle",
        "invert_round_rect",
    )

    /** `layout`（定位模式）合法取值；其余值回退 relative。 */
    val layoutModeValues: Set<String> = setOf("absolute", "flex", "relative")

    /** 文本水平对齐 `textAlign` 合法取值（button / textfield / text 共用）。 */
    val textAlignValues: Set<String> = setOf("left", "center", "right")

    // ===== 滚动容器枚举取值 =====

    /** 滚动方向 `direction` 合法取值。 */
    val scrollDirectionValues: Set<String> = setOf("vertical", "horizontal", "both")

    /** 滚动条显示模式 `scrollbarMode` 合法取值。 */
    val scrollbarModeValues: Set<String> = setOf("auto", "always", "never")

    /** pageview / carousel 指示器位置 `indicatorPosition` 合法取值。 */
    val indicatorPositionValues: Set<String> = setOf("bottom", "top", "left", "right")

    /** virtualGrid 排布流向 `flow` 合法取值。 */
    val virtualGridFlowValues: Set<String> = setOf("row-major", "column-major")

    /** zoomable 缩放焦点 `zoomFocus` 合法取值。 */
    val zoomFocusValues: Set<String> = setOf("mouse", "center")

    // ===== effects 块枚举取值（供 effects {} 块内补全；EffectPropContributor 解析） =====

    /** `effects { blend { mode } }` 混合模式合法取值，对应 runtime `GuiBlendMode`。 */
    val blendModeValues: Set<String> = setOf("normal", "multiply", "screen", "additive")

    /** `effects { flow { mode } }` 流光带模式合法取值。 */
    val flowModeValues: Set<String> = setOf("linear", "radial", "diagonal")

    /** `effects { energy { pattern } }` 能量场图案合法取值。 */
    val energyPatternValues: Set<String> = setOf("pulse", "wave", "arc", "plasma")

    /** `modifiers { textureLayer { sampling } }` 资源纹理采样模式合法取值。 */
    val textureSamplingValues: Set<String> = setOf("tile", "stretch", "nineSlice", "cover")

    /** `modifiers { materialOverlay { material } }` 内置程序材质族合法取值。 */
    val materialOverlayValues: Set<String> =
        setOf("stone", "rustIron", "woodGrain", "frost", "rune", "noise", "scratch")

    /** `modifiers { historyLayer { source } }` 历史采样来源合法取值。 */
    val historyLayerSourceValues: Set<String> = setOf("self", "backdrop")

    /** `modifiers { particleLayer { kind } }` 粒子效果族合法取值。 */
    val particleKindValues: Set<String> = setOf("sparks", "snow", "runeflux")

    /** `modifiers { particleLayer { emission } }` 发射模式合法取值。 */
    val particleEmissionValues: Set<String> = setOf("continuous", "burst")

    /** `modifiers { particleLayer { layer } }` 前景 / 背景层次合法取值。 */
    val particleLayerValues: Set<String> = setOf("front", "back")

    /** `modifiers { particleLayer { emitShape } }` FR-UI-032 发射形状合法取值（未声明时沿用 kind 默认形状）。 */
    val particleEmitShapeValues: Set<String> = setOf("point", "area", "edge", "border", "ring", "line")

    /** `modifiers { particleLayer { emitEdge } }` emitShape=edge 时的边合法取值。 */
    val particleEmitEdgeValues: Set<String> = setOf("top", "bottom", "left", "right")
}
